import { useEffect, useLayoutEffect, useRef, useState } from "react";
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import Button from "../../../components/buttons/Button";
import Image from "../../../components/images/Image";
import Shimmer from "../../AdminManagement/components/ViewAdminDetailsShimmer";
import useApi from "../../../services/useApi";
import useTranslation from "../../../components/CustomHooks/useTranslation";
import config from "../../../config/config";
import { useNavigate, useParams } from "react-router";
import { getFirefoxCssFix } from "../../../components/commonfunctions/getFirefoxCssFix";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../../store"
import { showToast } from "../../../components/toastMessage/toastSlice";
import { useSelector } from "react-redux";
import { adminContents } from "../../AdminManagement/components/AdminManagementContent";
import { NOTIFICATION_LANGUAGE_KEY_TO_DISPLAY } from "../../../components/utils/notificationLanguage";
import SecondaryButton from "../../../components/buttons/SecondaryButton";
import CustomModal from "../../../components/CustomModal";
import UpdatePasswordModal from './UpdatePasswordModal';
import UpdatePinModal from "./UpdatePinModal";
import NotificationPreferenceModal from "../../../components/CustomModal/NotificationPreferenceModal";

type PersonalDetails = {
  first_name: string;
  last_name: string;
  country_code: string;
  phone_number: string;
};

type ResidentialDetails = {
  house_no: string | null;
  floor_no: string | null;
  market_block_no: string | null;
  street_name: string;
  city_town: string;
  district: string | null;
  region: string | null;
  province: string;
  country: string;
  postal_code: string;
};

type AdminDetails = {
  personal_details: PersonalDetails;
  residential_details: ResidentialDetails;
};

type viewDetails = {
  id: string;
  user_code: string;
  ussd_code: string;
  first_name: string;
  last_name: string;
  country_code: string;
  phone_number: string;
  house_no: string | null;
  floor_no: string | null;
  market_block_no: string | null;
  street_name: string;
  city_town: string;
  district: string | null;
  region: string | null;
  province: string;
  country: string;
  postal_code: string;
  access_privileges: {
    [key: string]: string[];
  };
  status?: boolean;
  sop?: string;
  wallet_access?: string[];
  wallet_balance?: number;
  notification_type?: string[];
  notification_language?: string;
};

export default function ViewAdminProfile() {
  const { getAPI, PatchAPI } = useApi();
  const { translate, notEnglish, replace } = useTranslation(adminContents);
  const [selectedAction, setSelectedAction] = useState("");
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const navigate = useNavigate();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const userEmail = useSelector((state: RootState) => state.userEmail.email);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationModalLoading, setNotificationModalLoading] = useState(false);



  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        (detailsRef.current &&
          !detailsRef.current.contains(event.target as Node))
      ) {
        detailsRef.current.open = false;
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const [adminDetails, setAdminDetails] = useState<viewDetails>({
    id: "",
    user_code: "",
    ussd_code: "",
    first_name: "",
    last_name: "",
    country_code: "",
    phone_number: "",
    house_no: null,
    floor_no: null,
    market_block_no: null,
    street_name: "",
    city_town: "",
    district: null,
    region: null,
    province: "",
    country: "",
    postal_code: "",
    access_privileges: {},
    sop: "",
    wallet_access: [],
    wallet_balance: 0,
  });

  const fetchAdminDetails = async () => {
    setLoading(true);
    try {
      const response = await getAPI(`admins/email/${userEmail}`);
      if (!response?.error) {
        const temp = response?.data?.data;
        delete temp["created_at"];
        delete temp["updated_at"];
        setAdminDetails(temp);
      } else {
        navigate("/not-found", {
          state: { route: "/admin-management/list-of-admins" },
        });
      }
    } catch (error) {
      console.error("Error fetching admin details:", error);
    }
    setLoading(false);
  };

  useLayoutEffect(() => {
    fetchAdminDetails();
  }, []);

  const handleStatus = async (text?: string) => {
    setButtonLoading(true);
    try {
      let payload: { status?: boolean; extras?: { deactivate_reason: string } } = { status: true };
      let endPoint = `admins/${id}`;
      let message = ''
      switch (selectedAction) {
        case 'password':
          endPoint = `admins/reset-password/${id}`;
          payload = {}
          message = `New password has been sent to ${adminDetails.first_name} ${adminDetails.last_name} (${adminDetails.user_code}) via email successfully.`
          break;
        default:
          break;
      }

      const res = await PatchAPI(endPoint, payload);

      if (!res.error) {
        dispatch(
          showToast({
            type: "success",
            message: message,
          })
        );
        fetchAdminDetails();
      } else {
        dispatch(
          showToast({
            type: "error",
            message: res.data.message,
          })
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsOpen(false);
      setButtonLoading(false);
    }
  };

  const resetOptions = ["password", "PIN"];
  const handelActions = (option: string) => {
    setSelectedAction(option);
    if (option === 'password') {
      setShowPasswordModal(true);
      setIsOpen(false);
    } else if (option === 'PIN') {
      setShowPinModal(true)
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };

  // Handle notification preference save
  const handleNotificationSave = async (notificationType: string) => {
    setNotificationModalLoading(true);
    
    try {
      const response = await PatchAPI(`admins/${adminDetails.id}`, {
        notification_type: [notificationType],
      });

      if (!response.error) {
        dispatch(
          showToast({
            type: "success",
            message: `Success! You'll now receive all notifications via ${notificationType}.`,
          })
        );
        // Update adminDetails with the new notification_type
        setAdminDetails(prev => ({
          ...prev,
          notification_type: [notificationType]
        }));
        setShowNotificationModal(false);
      } else {
        dispatch(
          showToast({
            type: "error",
            message: response.data?.message || "Failed to update notification preference",
          })
        );
      }
    } catch (error) {
      console.error("Error updating notification preference:", error);
      dispatch(
        showToast({
          type: "error",
          message: "An error occurred while updating notification preference",
        })
      );
    } finally {
      setNotificationModalLoading(false);
    }
  };

  // Get the current notification type from adminDetails
  const getCurrentNotificationType = () => {
    if (adminDetails.notification_type && adminDetails.notification_type.length > 0) {
      if(adminDetails.notification_type[0] === "email") {
        return "Email Address";
      } else if(adminDetails.notification_type[0] === "sms") {
        return "Phone Number";
      } else if(adminDetails.notification_type[0] === "push") {
        return "In App Push";
      }
      return adminDetails.notification_type[0];
    }
    return "Email Address";
  };
  const getModelText = () => {
    switch (selectedAction) {
      case "password":
        return {
          title: translate("Reset Password"),
          description: replace(
            `Would you like to proceed with resetting the password for {{admin_name}} ({{admin_user_code}})?`,
            {
              admin_name: `${adminDetails.first_name} ${adminDetails.last_name}`,
              admin_user_code: adminDetails.user_code,
            }
          ),
          buttonName: translate("Reset"),
        };
      default:
        return {};
    }
  };

  const getFullName = () => {
    return `${adminDetails.first_name}${adminDetails.last_name ? ` ${adminDetails.last_name}` : ''}`;
  };

  const getFullPhoneNumber = () => {
    if (adminDetails.country_code && adminDetails.phone_number) {
      return `${adminDetails.country_code} ${adminDetails.phone_number}`;
    }
    return "-";
  };

  const getFullAddress = () => {
    const parts = [
      adminDetails.house_no,
      adminDetails.floor_no,
      adminDetails.market_block_no,
      adminDetails.street_name,
      adminDetails.city_town,
      adminDetails.district,
      adminDetails.region,
      adminDetails.province,
      adminDetails.country,
      adminDetails.postal_code
    ].filter(Boolean);

    return parts.join(", ");
  };

  return (
    <div dir={notEnglish ? "rtl" : "ltr"}>
      <Breadcrumbs
        heading="View Profile"
      />

      {!loading ? (
        <div className="mt-1.5 bg-white h-maintableheight rounded-lg flex flex-col">
          <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-[#E5E9EB] flex flex-wrap gap-3 justify-between items-start sm:items-center">
            <div className="min-w-0">
              <h1 title={getFullName()} data-testid="user-name" className="text-[20px] sm:text-heading2 truncate font-semibold">
                {getFullName() || "-"}
              </h1>
              <p title={adminDetails?.user_code} data-testid="user-code" className="text-subTitle truncate">
                {adminDetails?.user_code || "-"} | {adminDetails?.ussd_code || "-"}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <Button
                buttonName={translate("View SOP")}
                className="py-2 sm:py-2.5 h-fit text-xs sm:text-sm border border-p_primary cursor-pointer font-semibold px-3 sm:px-4 w-fit"
                testId="view-sop"
                buttonOnclick={() => {
                  const fileUrl = config?.cdnURL + 'public/' + adminDetails?.sop;
                  window.open(fileUrl, "_blank");
                }}
              />
              <SecondaryButton
                buttonName={translate("View Transaction History")}
                className="py-2 sm:py-2.5 h-fit px-3 sm:px-4 cursor-pointer text-xs sm:text-sm font-semibold w-fit"
                handleClick={() => navigate(`/view-profile/transaction-details`)}
                testId="view-transaction-history"
              />
              <Image src="editImg" className="w-6 h-6 cursor-pointer" onClick={() => navigate(`/view-profile/edit-profile`)} />
              <Image src="settings" className="w-6 h-6 cursor-pointer" onClick={() => setShowNotificationModal(true)} />
              <details ref={detailsRef} className="relative">
                <summary className="px-2 py-2 bg-primary-500 text-white rounded-full font-semibold cursor-pointer w-[40px] h-[40px] flex items-center justify-center">
                  <Image src="more-vertical" objectFit={"object-contain"} testId="action-btn" />
                </summary>
                <div className="absolute z-10 bg-white shadow rounded-md w-[190px] mt-1 left-0 sm:left-auto sm:right-0">
                  <ul className="p-2 border border-s_dark_hover rounded-lg">
                    {resetOptions.map((item) => (
                      <li
                        onClick={() => handelActions(item)}
                        data-testid={`Update ${item}`}
                        key={item} className="text-primary_text hover:text-p_primary cursor-pointer p-2.5 hover:rounded-lg hover:bg-neutral-100 rounded-xs capitalize">
                        {translate(`Update ${(item)}`)}
                      </li>))}
                  </ul>
                </div>
              </details>
            </div>
          </div>
          {adminDetails?.user_code?.startsWith("ADM") && <div className="w-full flex flex-col sm:flex-row py-4 sm:py-6 px-4 sm:px-[25px] gap-4 sm:gap-6">
            <div className="w-full sm:w-1/2 flex justify-between items-center px-[15px] h-[90px] sm:h-[106px] bg-p_primary rounded-lg">
              <div>
                <h1 className="text-white text-[14px] sm:text-[18px] font-semibold">E Top Up Wallet Balance</h1>
                <h1 data-testid="etop_up_wallet_balance" className="text-white text-[18px] sm:text-[24px] font-semibold pt-1 sm:pt-2">AFA {adminDetails?.wallet_balance}</h1>
              </div>
              <Image src="etopup-wallet" className="w-[90px] sm:w-[126px] h-14 sm:h-20" />
            </div>
            <div className="w-full sm:w-1/2 flex justify-between items-center px-[15px] h-[90px] sm:h-[106px] bg-disable_stroke rounded-lg">
              <div>
                <h1 className="text-white text-[14px] sm:text-[18px] font-semibold">Digital Wallet Balance</h1>
                <h1 className="text-white text-[18px] sm:text-[24px] font-semibold pt-1 sm:pt-2">AFA 0.00</h1>
              </div>
              <Image src="digital-wallet" className="w-[90px] sm:w-[126px] h-14 sm:h-20" />
            </div>
          </div>}
          <div
            className={`flex-grow px-4 sm:px-6 pb-4 my-4 h-viewAdminHeight ${getFirefoxCssFix(
              "scroll"
            )} overflow-y-auto`}
          >
            {/* Personal Details Section */}
            <div className="mt-2">
              <h1 className="text-heading3 underline underline-offset-4">
                {translate("Personal Details")}
              </h1>
              <div className="mt-3.5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <h1 className="text-body2 text-primary_text">
                      {translate("User Role")}
                    </h1>
                    <p
                      data-testid="user_role"
                      dir="ltr"
                      title={adminDetails.user_code?.startsWith('SADM') ? 'Super Admin' : adminDetails.user_code?.startsWith('ADM') ? 'Admin' : '-'}
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
                    >
                      {adminDetails.user_code?.startsWith('SADM') ? 'Super Admin' : adminDetails.user_code?.startsWith('ADM') ? 'Admin' : '-'}
                    </p>
                  </div>
                  <div>
                    <h1 className="text-body2 text-primary_text">
                      {translate("Official Email Address")}
                    </h1>
                    <p
                      data-testid="official_email_address"
                      dir="ltr"
                      title={userEmail}
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
                    >
                      {userEmail || "-"}
                    </p>
                  </div>
                  <div>
                    <h1 className="text-body2 text-primary_text">
                      {translate("Phone Number")}
                    </h1>
                    <p
                      data-testid="phone_number"
                      dir="ltr"
                      title={getFullPhoneNumber()}
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden"
                    >
                      {getFullPhoneNumber() || "-"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Residential Details Section */}
            <div className="mt-6">
              <h1 className="text-heading3 underline underline-offset-4">
                {translate("Residential Details")}
              </h1>
              <div className="mt-3.5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <h1 className="text-body2 text-primary_text">
                      {translate("Address")}
                    </h1>
                    <p
                      data-testid="address"
                      dir="ltr"
                      title={getFullAddress()}
                      className="text-body1 text-primary_text max-w-full text-ellipsis overflow-hidden text-justify"
                    >
                      {getFullAddress() || "-"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Preferred Notification Language Section */}
            <div className="mt-6">
              <h1 className="text-heading3 underline underline-offset-4">
                {translate("Preferred Notification Language")}
              </h1>
              <div className="mt-3.5">
                <h1 className="text-body2 text-primary_text">{translate("Language")}</h1>
                <p data-testid="notification-language" className="text-body1 text-primary_text">
                  {NOTIFICATION_LANGUAGE_KEY_TO_DISPLAY[adminDetails?.notification_language || ""] || "-"}
                </p>
              </div>
            </div>

            {/* privileges section starts */}

            {/* tabs section  */}
            <div className="pt-4 sticky -top-4 bg-white">
              <div className="flex items-end">
                {/* Blue Border Section */}
                <p className="bg-white px-2.5 py-2.5 cursor-pointer text-heading4-semi text-p_primary border-b-[2px] border-p_primary">
                  {translate("Access Module")}
                </p>

                {/* Gray Border Section */}
                <div className="flex-1 border-b-[2px] border-[#EEEEEE]"></div>
              </div>
            </div>

            {/* tabs section  */}
            <div className="mt-6">
              {Object.keys(adminDetails?.access_privileges).map(
                (mainPrivilege, index) => (
                  <div key={index} className="border-b border-outline py-2">
                    <h1 className="text-large p-2 mb-1.5">
                      {translate(mainPrivilege)}
                    </h1>
                    <div className="-mx-2.5 flex flex-wrap">
                      {adminDetails?.access_privileges[mainPrivilege].map(
                        (subPrivilege, i) => (
                          <p
                            key={i}
                            className="w-full sm:w-1/2 md:w-1/3 mb-2 text-body1 text-primary_text px-[18px] py-2 flex-wrap "
                          >
                            {translate(subPrivilege)}
                          </p>
                        )
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
            {/* privileges section starts */}

            {/* wallet section  */}
            <div className="mt-6 ">
              <p className="text-heading3 underline underline-offset-4">
                Wallet Access
              </p>
              <p className="mt-2 text-body1">
                {adminDetails?.wallet_access?.includes("etop_up") ? "eTop Up" : "-"}
              </p>
            </div>
            {/* wallet section  */}
          </div>
        </div>
      ) : (
        <Shimmer />
      )}
      {isOpen && (
        <CustomModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          title={getModelText().title}
          subText={getModelText().description}
          primaryButtonText={getModelText().buttonName || ''}
          secondaryButtonText={translate('Cancel')}
          handelSubmit={(text?: string) => handleStatus(text)}
          buttonColor={selectedAction === 'deactivate' ? "bg-negative" : selectedAction === 'activate' ? "bg-positive" : 'bg-p_primary'}
          textArea={selectedAction === 'deactivate'}
          buttonLoading={buttonLoading}
          buttonWidth={(selectedAction === 'password' || selectedAction === 'PIN') && notEnglish ? 'w-fit' : (selectedAction === 'password' || selectedAction === 'PIN') ? 'w-[90px]' : ''}
        />
      )}
      {showPasswordModal && (
        <UpdatePasswordModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
        />
      )}
      {showPinModal && (
        <UpdatePinModal
          isOpen={showPinModal}
          onClose={() => setShowPinModal(false)}
        />
      )}
      {/* Notification Preference Modal */}
      {showNotificationModal && <NotificationPreferenceModal
        isOpen={showNotificationModal}
        onSave={handleNotificationSave}
        loading={notificationModalLoading}
        defaultSelected={getCurrentNotificationType()}
        headerText="Settings"
        showTabs={true}
        closeOnOutsideClick={true}
        onClose={() => setShowNotificationModal(false)}
        buttonText="Update"
      />}
    </div>
  );
}
